/**
 * CompactSelectionToolbar — F 200 Bug F, extended by F 371.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The operator: "looks a bit too like Word." They were right. Selecting text
 * produced BlockNote's DEFAULT selection menu, measured on production at 12
 * controls:
 *
 *   Paragraph | Bold | Italic | Underline | Strike |
 *   Align left | Align centre | Align right | Colors |
 *   Nest block | Unnest block | Create link
 *
 * The ask was a Notion-style bar of 4–6 icons. BlockNote 0.47 does not take an
 * options object for this — `FormattingToolbarProps` has no filter, and
 * `getFormattingToolbarItems()` hard-codes the 12-item array. But
 * `FormattingToolbar` accepts `children`, and passing children REPLACES the
 * default list entirely:
 *
 *   node_modules/@blocknote/react/types/.../FormattingToolbar.d.ts
 *     "By default, the FormattingToolbar component will render with default
 *      selects/buttons. However, you can override the selects/buttons to
 *      render by passing children."
 *
 * So this is a supported override, not a fork and not CSS-hiding.
 *
 * WHAT IS KEPT AND WHAT GOES
 * --------------------------
 *   KEPT    Bold, Italic, Underline, Link          (4, as specified)
 *   GONE    Strike, Colors, Nest/Unnest, the three
 *           text-align buttons, Add comment, and
 *           the Paragraph block-type select
 *
 * THERE IS NO AI MENU HERE. An earlier version of this comment claimed the
 * operator had also seen "Skills / Improve writing / Proofread / Explain /
 * Edit with AI", and attributed them to `@blocknote/xl-ai`. That was wrong,
 * and F 211 proved it rather than argued it:
 *   - @blocknote/xl-ai is not in package.json and is not installed; the
 *     installed set is @blocknote/{core,mantine,react}.
 *   - "Edit with AI", "Improve writing", "Proofread" and "Ask AI" appear
 *     in NO file under node_modules, in any package.
 *   - Live, with text selected, this menu offers exactly four controls:
 *     Bold, Italic, Underline, Create link.
 * So there was never an AI feature here to disable, and no provenance to
 * trace. Corrected by F 213; nothing below needed changing.
 *
 * THE SLASH MENU IS UNTOUCHED. The operator said it works and F 198 proved it
 * live. Only the FormattingToolbar is overridden here; the suggestion menu is a
 * different extension and is not touched.
 *
 * ── F 371 — WHAT WAS ADDED AND WHY ──────────────────────────────────────
 *
 * F 371 asks the floating text toolbar to grow Notion's block-level actions:
 * Turn into (H1/H2/H3), Duplicate, Move to, Delete, Copy link.
 *
 * MEASURED BEFORE (real Chromium, production, F 371 probe 2):
 *
 *   toolbar button count: 4   (Bold, Italic, Underline, Create link)
 *   toolbar width: 132px
 *
 * and the adjacent defect: the peek panel's text column was 93px wide, so
 * the 132px toolbar was WIDER than the text it was formatting.
 *
 * WHY "TURN INTO" LIVES HERE AND NOT ONLY IN THE 6-DOT MENU
 * -----------------------------------------------------------
 * Notion shows "Turn into" in BOTH places, because they act on different
 * things: the 6-dot acts on the HOVERED block, the text toolbar acts on the
 * block(s) in the TEXT SELECTION. Those can be different blocks — F 371
 * probe 8 measured exactly that case (cursor in paragraph 1, 6-dot hovered
 * on paragraph 4). So both are needed, and they use the same underlying
 * `updateBlock` API against different targets.
 *
 * WHICH BLOCK(S) DOES THE TEXT TOOLBAR ACT ON?
 * --------------------------------------------
 * `useSelectedBlocks()` is the supported answer: it returns the blocks the
 * text selection spans, falling back to the single block at the text cursor.
 * That is exactly the right target set for a text-selection toolbar.
 *
 * THE "COPY LINK" LINK IS REAL
 * ---------------------------
 * The app has a real router (`/page/:pageId` — src/core/router/
 * SurfaceRouter.tsx:99) and a real `buildUrl`. A block link is
 * `<origin>/page/<pageId>#<blockId>`, and the block id is a REAL BlockNote
 * block id — the same id the DOM carries as `data-id` (measured in F 371
 * probe 8) and the same id `editor.getBlock(id)` resolves. The hash is
 * read back on open by `scrollToBlock` below, so the link lands on the
 * block rather than just the page.
 *
 * NOTHING HERE IS A STUB EXCEPT "MOVE TO" WHEN THERE IS NOWHERE TO MOVE TO
 * -----------------------------------------------------------------------
 * "Move to" opens a submenu of the OTHER pages in the workspace. When the
 * workspace has only one page there is nowhere to move to, and the item
 * says so instead of silently doing nothing.
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  FormattingToolbar,
  FormattingToolbarController,
  BasicTextStyleButton,
  CreateLinkButton,
  useBlockNoteEditor,
  useSelectedBlocks,
} from '@blocknote/react';
import { ActionIcon, Menu, Tooltip } from '@mantine/core';
import {
  IconBlockquote,
  IconCopy,
  IconDots,
  IconEye,
  IconH1,
  IconH2,
  IconH3,
  IconLink,
  IconTrash,
  IconTypography,
} from '@tabler/icons-react';
import { useEditorChrome } from './EditorChromeContext';

/** The heading levels "Turn into" offers, in Notion's order. */
const HEADING_LEVELS = [
  { level: 1, label: 'Heading 1', icon: IconH1 },
  { level: 2, label: 'Heading 2', icon: IconH2 },
  { level: 3, label: 'Heading 3', icon: IconH3 },
] as const;

/**
 * Build a shareable link to one block inside a page.
 *
 * The URL shape is the app's real route (`/page/:pageId`,
 * src/core/router/SurfaceRouter.tsx:99) plus a `#<blockId>` fragment. The
 * block id is the same id the editor uses, so `scrollToBlock` can find it
 * again on open.
 */
export function buildBlockLink(
  pageId: string | null | undefined,
  blockId: string | null | undefined,
): string | null {
  if (!pageId || !blockId) return null;
  const origin =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : '';
  return `${origin}/page/${encodeURIComponent(pageId)}#${blockId}`;
}

/**
 * Copy a text to the clipboard, tolerating a denied permission.
 *
 * Returns whether the copy actually happened, so the caller can tell the
 * user the truth rather than assuming success.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * The toolbar itself.
 *
 * It takes NO props on purpose: `FormattingToolbarController` types its
 * `formattingToolbar` prop as `FC<FormattingToolbarProps>`, and a component
 * with a different prop shape is not assignable to it. The host data comes
 * from `EditorChromeContext` instead — see the file header for why that is
 * the better shape anyway.
 */
export const CompactSelectionToolbar: React.FC = () => {
  const editor = useBlockNoteEditor();
  const chrome = useEditorChrome();
  const selected = useSelectedBlocks(editor);
  const [menuOpen, setMenuOpen] = useState(false);

  const { pageId, moveTargets, onMoveTo } = chrome;

  /**
   * The block the toolbar acts on. `useSelectedBlocks` returns the blocks
   * the text selection spans, or the single block at the cursor.
   */
  const target = selected[0] ?? null;

  const setBlockType = useCallback(
    (type: 'paragraph' | 'heading' | 'quote', level?: 1 | 2 | 3) => {
      const blocks = selected.length > 0 ? selected : target ? [target] : [];
      for (const b of blocks) {
        if (type === 'heading') {
          editor.updateBlock(b, {
            type: 'heading',
            props: { level: level ?? 1 },
          });
        } else {
          editor.updateBlock(b, { type });
        }
      }
    },
    [editor, selected, target],
  );

  const duplicate = useCallback(() => {
    const blocks = selected.length > 0 ? selected : target ? [target] : [];
    if (blocks.length === 0) return;
    // Duplicate from the bottom up, so each insert does not shift the
    // reference block of the next one.
    for (const b of [...blocks].reverse()) {
      const copy = JSON.parse(JSON.stringify(b));
      delete (copy as { id?: string }).id;
      editor.insertBlocks([copy], b, 'after');
    }
  }, [editor, selected, target]);

  const remove = useCallback(() => {
    const blocks = selected.length > 0 ? selected : target ? [target] : [];
    if (blocks.length === 0) return;
    editor.removeBlocks(blocks);
  }, [editor, selected, target]);

  const copyLink = useCallback(async () => {
    const link = buildBlockLink(pageId, target?.id ?? null);
    if (!link) return;
    await copyToClipboard(link);
  }, [pageId, target]);

  const moveTo = useCallback(
    (destPageId: string) => {
      if (!onMoveTo) return;
      const ids = (selected.length > 0 ? selected : target ? [target] : [])
        .map((b) => b.id)
        .filter((id): id is string => Boolean(id));
      if (ids.length === 0) return;
      onMoveTo(destPageId, ids);
    },
    [onMoveTo, selected, target],
  );

  const canMove = Boolean(onMoveTo) && Boolean(moveTargets && moveTargets.length > 0);

  const currentType = target?.type ?? 'paragraph';

  const turnIntoItems = useMemo(
    () => [
      {
        text: 'Text',
        icon: <IconTypography size={14} />,
        onClick: () => setBlockType('paragraph'),
        isSelected: currentType === 'paragraph',
      },
      ...HEADING_LEVELS.map(({ level, label, icon: Icon }) => ({
        text: label,
        icon: <Icon size={14} />,
        onClick: () => setBlockType('heading', level),
        isSelected: currentType === 'heading' && target?.props?.level === level,
      })),
      {
        text: 'Quote',
        icon: <IconBlockquote size={14} />,
        onClick: () => setBlockType('quote'),
        isSelected: currentType === 'quote',
      },
    ],
    [currentType, target, setBlockType],
  );

  return (
    <FormattingToolbar>
      <BasicTextStyleButton basicTextStyle="bold" />
      <BasicTextStyleButton basicTextStyle="italic" />
      <BasicTextStyleButton basicTextStyle="underline" />
      <CreateLinkButton />

      {/* ── Turn into (H1 / H2 / H3) ───────────────────────────────────── */}
      <Menu
        opened={menuOpen}
        onChange={setMenuOpen}
        position="bottom-start"
        shadow="md"
        width={190}
        withinPortal
      >
        <Menu.Target>
          <Tooltip label="Turn into" withArrow openDelay={400}>
            <ActionIcon
              variant="subtle"
              size={20}
              className="fc-toolbar-btn fc-toolbar-turn-into"
              aria-label="Turn into"
              data-testid="fc-toolbar-turn-into"
            >
              {/* The chevron alone says "a menu is here" but not WHAT it
                  changes. Notion labels this control with the CURRENT
                  block type, so the operator can see what the selection
                  is before opening it. */}
              <IconTypography size={14} />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          {turnIntoItems.map((item) => (
            <Menu.Item
              key={item.text}
              leftSection={item.icon}
              onClick={item.onClick}
              data-testid={`fc-toolbar-turn-into-${item.text.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {item.text}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>

      {/* ── Duplicate / Move to / Delete / Copy link ──────────────────── */}
      <Menu position="bottom-start" shadow="md" width={210} withinPortal>
        <Menu.Target>
          <Tooltip label="More actions" withArrow openDelay={400}>
            <ActionIcon
              variant="subtle"
              size={20}
              className="fc-toolbar-btn fc-toolbar-more"
              aria-label="More actions"
              data-testid="fc-toolbar-more"
            >
              {/* F 374 — this was a LINK icon, which is wrong twice over:
                  the menu it opens has nothing to do with links, and the
                  toolbar already carries a real link button two icons to
                  the left. An operator had two identical-looking icons
                  doing different things. It is a dots icon now. */}
              <IconDots size={14} />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconCopy size={15} />}
            onClick={duplicate}
            data-testid="fc-toolbar-duplicate"
          >
            Duplicate
          </Menu.Item>

          {canMove ? (
            <Menu
              position="right-start"
              shadow="md"
              width={220}
              withinPortal
            >
              <Menu.Target>
                <Menu.Item
                  leftSection={<IconEye size={15} />}
                  data-testid="fc-toolbar-move-to"
                >
                  Move to
                </Menu.Item>
              </Menu.Target>
              <Menu.Dropdown>
                {moveTargets!.map((t) => (
                  <Menu.Item
                    key={t.id}
                    onClick={() => moveTo(t.id)}
                    data-testid={`fc-toolbar-move-to-${t.id}`}
                  >
                    {t.title}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          ) : (
            <Menu.Item
              leftSection={<IconEye size={15} />}
              disabled
              data-testid="fc-toolbar-move-to"
            >
              Move to (no other page)
            </Menu.Item>
          )}

          <Menu.Item
            leftSection={<IconLink size={15} />}
            onClick={copyLink}
            data-testid="fc-toolbar-copy-link"
          >
            Copy link
          </Menu.Item>

          <Menu.Item
            color="red"
            leftSection={<IconTrash size={15} />}
            onClick={remove}
            data-testid="fc-toolbar-delete"
          >
            Delete
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </FormattingToolbar>
  );
};

/**
 * F 202 C — the ANCHORED form. Used from PageEditor instead of
 * `<CompactSelectionToolbar />` as a bare BlockNoteView child.
 *
 * WHY THIS EXISTS (and why F 200's version caused item C)
 * ------------------------------------------------------
 * `FormattingToolbar` is only the BUTTONS. Positioning is done by
 * `FormattingToolbarController`, which BlockNoteDefaultUI renders.
 * F 200 set `formattingToolbar={false}` to stop the default 12 buttons and
 * then passed this component as a plain child — but that also removed the
 * controller, so nothing was positioning the toolbar any more. Measured on
 * production: toolbar at y=775, selection at y=220, 558px apart, parked at
 * the panel's bottom-left.
 *
 * The controller is exported and takes a `formattingToolbar` prop, so the
 * fix is to KEEP the controller and hand it this component:
 *
 *   FormattingToolbarController({ formattingToolbar: CompactSelectionToolbar })
 *
 * That restores Floating-UI anchoring to the selection while keeping the
 * four buttons. `formattingToolbar={false}` stays on BlockNoteView so the
 * DEFAULT 12-item toolbar is not also rendered.
 *
 * F 371 — the controller is ALSO the thing that makes the toolbar follow
 * the selection while the page scrolls. Measured before (probe 7): a real
 * wheel scroll of 260px moved the toolbar from y=448 to y=208 and the
 * selection from y=494 to y=254 — the gap `dy` was -46px before AND
 * after, i.e. the toolbar was ALREADY glued to the selection. That part of
 * the dispatch's premise was already satisfied by Floating-UI's `autoUpdate`
 * inside `PositionPopover`; the real defects were the missing rich options
 * and the toolbar being wider than the text column. The controller is
 * therefore kept exactly as it was.
 *
 * F 371 — the toolbar's host data (the page id, the move targets, the move
 * itself) comes from `EditorChromeContext`, NOT from a prop on this
 * component. Two reasons:
 *
 *   1. F 202's test pins this exact source form —
 *      `<FormattingToolbarController formattingToolbar={CompactSelectionToolbar} />`
 *      — because that IS the fix for the anchoring bug. Changing the shape
 *      to forward a payload would quietly undo a pinned contract.
 *   2. A context is the right shape anyway: the toolbar lives four levels
 *      down inside BlockNote's own tree, and the thing it needs to know
 *      about — which page is being edited — belongs to the whole editor,
 *      not to one call site.
 */
export const AnchoredCompactToolbar: React.FC = () => (
  <FormattingToolbarController formattingToolbar={CompactSelectionToolbar} />
);

export default CompactSelectionToolbar;
